import { useEffect, useState } from "react";
import { useT } from "../i18n";
import { tagVon, uhrVon } from "../daten/vorbereitung";
import { zahlAnzeige } from "../daten/zahlfeld";
import { dauer, stunden } from "../daten/einsatzlage";
import type { Sicht } from "../daten/einsatzlage";
import "../stile/einsatzzeit.css";
import "../stile/einsatz.css";

/* ══════════════════════════════════════════════════════════════════
   Die Uhr des Einsatzes — was gerade läuft, groß.

   ```
   ┌ ANFAHRT                ● läuft   ┐
   │ 0:32 h               seit 06:48  │
   └ Solarpark Nord, Molbergen        ┘
   ```

   **Die Zahl ist 40 px Mono, tabellarisch** (Arbeitsgrundlage,
   „Layout-Regeln"): Sie wird auf dem Hof in der Sonne gelesen, und eine
   Ziffer, die beim Weiterzählen die Breite wechselt, zittert.

   **Der Zustand ist Form und Wort, nie nur Farbe.** „● läuft" hat einen
   atmenden Punkt und das Wort; „✓ bestätigt" den Haken und das Wort.

   **Was die Uhr zählt:**

   | Lage | groß | darunter |
   |---|---|---|
   | noch nicht losgefahren | geplanter Beginn | Tag und Ort |
   | unterwegs | Dauer der Anfahrt | seit wann, wohin |
   | auf der Baustelle | heute auf der Baustelle, mit der laufenden | Anfahrt, Zähler |
   | Pause | heute auf der Baustelle | seit wann Pause ist |
   | Tag ohne Bestätigung | heute auf der Baustelle | der Grund |
   | Tag bestätigt | heute auf der Baustelle | von wem |
   | beim Kunden (Mietgerät) | Tag x von n | Rücknahme wann |
   | abgeschlossen | ✓ | — |

   Die Anfahrt zählt nicht in die Stunden des Tages: Sie wird erfasst, nicht
   abgerechnet (09-mobile.md, „Die Anfahrt").
   ══════════════════════════════════════════════════════════════════ */

/** Die Uhr des Geräts — im Takt, solange etwas läuft. */
export function useJetzt(takt: number | null): number {
  const [jetzt, setzeJetzt] = useState(() => Date.now());
  useEffect(() => {
    if (takt === null) return undefined;
    setzeJetzt(Date.now());
    const uhr = window.setInterval(() => setzeJetzt(Date.now()), takt);
    return () => window.clearInterval(uhr);
  }, [takt]);
  return jetzt;
}

const sekundenSeit = (iso: string, jetzt: number): number => {
  const d = new Date(iso).getTime();
  return Number.isFinite(d) ? Math.max(0, (jetzt - d) / 1000) : 0;
};

/**
 * Wie weit die Vorbereitung ist — für die Uhr, solange der Einsatz in
 * „Vorbereiten" steht (`bausteine/Checkliste.tsx`, `vorbereitungsfortschritt`).
 * `beantwortet` und `offen` sind `null`, wenn der Stand nicht zu haben ist:
 * Er liegt als Entwurf im Gerät dessen, der vorbereitet — wer den Einsatz
 * eines anderen ansieht, erfährt nur, wie viel zu tun ist.
 */
export type Vorbereitungsuhr = {
  gesamt: number;
  pflichtGesamt: number;
  beantwortet: number | null;
  offen: number | null;
};

export function Einsatzuhr({ sicht, jetzt, beginnIso, endeIso, ort, anfahrtSekunden, arbeitVon, arbeitBis, vorbereitung }: {
  sicht: Sicht;
  jetzt: number;
  /** Der geplante Beginn — solange noch nichts läuft. */
  beginnIso: string | null;
  endeIso: string | null;
  /** Wohin es geht: Einsatzort und Ortschaft in einer Zeile. */
  ort: string;
  /** Die heutige Anfahrt, wenn sie schon zu Ende ist. */
  anfahrtSekunden: number | null;
  /** Wann heute die Arbeit begann und endete — für „07:21 – 16:58". */
  arbeitVon: string | null;
  arbeitBis: string | null;
  /** Der Stand der Vorbereitung — nur in der Stufe „Vorbereiten" gelesen. */
  vorbereitung?: Vorbereitungsuhr | null;
}) {
  const t = useT();
  const s = sicht;
  const tag = s.einsatztag;
  const laeuft = s.phase;
  const heuteSek = (tag?.sekunden ?? 0) + (laeuft?.art === "arbeit" ? sekundenSeit(laeuft.seit, jetzt) : 0);
  const zaehler = tag?.zaehlerEnde ?? tag?.zaehlerBeginn ?? null;

  let titel: string;
  let gross: string;
  let einheit = "";
  let rechts: string | null = null;
  let zustand: "laeuft" | "ruhe" | "fertig" | "wartet" = "ruhe";
  /** Das Wort neben dem Haken, wenn `fertig` — „bestätigt", in der Vorbereitung „vollständig". */
  let fertigWort = t("einsatz.uhr.bestaetigt");
  /** Steht groß ein Satz statt einer Zahl? Dann kleiner gesetzt. */
  let satz = false;
  let unter: string[] = [];

  if (laeuft?.art === "anfahrt") {
    titel = t("einsatz.uhr.anfahrt");
    gross = dauer(sekundenSeit(laeuft.seit, jetzt));
    einheit = t("einsatz.uhr.h");
    rechts = t("einsatz.uhr.seit", { uhr: uhrVon(laeuft.seit) });
    zustand = "laeuft";
    unter = [ort];
  } else if (laeuft?.art === "arbeit") {
    titel = t("einsatz.uhr.baustelle");
    gross = dauer(heuteSek);
    einheit = t("einsatz.uhr.h");
    rechts = t("einsatz.uhr.seit", { uhr: uhrVon(laeuft.seit) });
    zustand = "laeuft";
    unter = [
      anfahrtSekunden !== null ? t("einsatz.uhr.anfahrtWar", { dauer: dauer(anfahrtSekunden) }) : "",
      zaehler !== null ? t("einsatz.uhr.zaehler", { stand: zahlAnzeige(zaehler, 1) }) : "",
    ];
  } else if (s.zustand === "abgeschlossen") {
    titel = t("einsatz.uhr.abgeschlossen");
    gross = t("einsatz.uhr.haken");
    zustand = "fertig";
    unter = [s.mitBediener && tag && tag.eintraege > 0
      ? t("einsatz.uhr.heuteStunden", { stunden: stunden(heuteSek) }) : ""];
  } else if (s.mitBediener && tag && tag.eintraege > 0 && !tag.bestaetigtAm && !tag.ohneBestaetigungGrund) {
    /* **Die Pause** (18.09.2026): Ein Ende, und der Tag ist nicht zu. Groß
       steht, was heute schon auf der Baustelle war; rechts, seit wann Pause ist. */
    titel = t("einsatz.uhr.pause");
    gross = stunden(heuteSek);
    einheit = t("einsatz.uhr.h");
    rechts = arbeitBis ? t("einsatz.uhr.seit", { uhr: uhrVon(arbeitBis) }) : null;
    unter = [t("einsatz.uhr.heuteStunden", { stunden: stunden(heuteSek) })];
  } else if (s.mitBediener && tag && tag.eintraege > 0) {
    titel = t(tag.bestaetigtAm ? "einsatz.uhr.tagBestaetigt" : "einsatz.uhr.tagOhne", {
      nr: s.tag.nr ?? "", gesamt: s.tag.gesamt,
    });
    gross = stunden(heuteSek);
    einheit = t("einsatz.uhr.h");
    zustand = tag.bestaetigtAm ? "fertig" : "ruhe";
    rechts = arbeitVon ? t("einsatz.uhr.vonBis", { von: uhrVon(arbeitVon), bis: arbeitBis ? uhrVon(arbeitBis) : "" }) : null;
    unter = [tag.bestaetigtAm
      ? t("einsatz.uhr.bestaetigtVon", { wer: tag.bestaetigtVon, uhr: uhrVon(tag.bestaetigtAm) })
      : t("einsatz.uhr.ohneGrund", { grund: tag.ohneBestaetigungGrund ?? "" })];
  } else if ((s.uebergabe.erledigt || (s.mitBediener && !s.uebergabeNoetig)) && s.tag.nr !== null) {
    /* Der Morgen eines Folgetags (oder das Mietgerät beim Kunden): Es läuft
       nichts, also steht groß, was ist — und nicht eine Zahl, die man für
       eine Uhrzeit halten könnte. **Ohne Übergabeprotokoll** (0126) gibt es
       kein `uebergabe.erledigt`, das den Einsatz eröffnet — dann zählt der Tag
       selbst. */
    titel = t("einsatz.uhr.tagVon", { nr: s.tag.nr, gesamt: s.tag.gesamt });
    gross = t(s.mitBediener ? "einsatz.uhr.nochNichts" : "einsatz.uhr.beimKunden");
    satz = true;
    unter = [
      endeIso ? t("einsatz.uhr.zurueckAm", { tag: tagVon(endeIso), uhr: uhrVon(endeIso) }) : "",
      s.mitBediener && s.letzterZaehler
        ? t("einsatz.uhr.zaehler", { stand: zahlAnzeige(s.letzterZaehler.stand, 1) }) : "",
    ];
  } else if (s.zustand === "vorbereiten") {
    /* **Die Stufe „Vorbereiten" hat Inhalt** (20.09.2026, abends). Luca: *„Der
       Zustand ‚Vorbereiten' hat da ja irgendwie nichts drin"* — hier stand
       nur „Beginn 07:00". Jetzt steht groß, wie weit die Checkliste ist, und
       darunter, was noch fehlt; rechts bleibt der Beginn, damit niemand die
       Uhrzeit suchen muss. Der Stand ist der **dieses Geräts** — die Antworten
       gehen erst mit dem Abschluss zum Server (`HandyVorbereitung.tsx`); wer
       den Einsatz eines anderen ansieht, bekommt nur die Zahl der
       Pflichtangaben und keinen erfundenen Fortschritt. */
    titel = t("einsatz.uhr.vorbereitung");
    rechts = beginnIso ? t("einsatz.uhr.tagUhr", { tag: tagVon(beginnIso), uhr: uhrVon(beginnIso) }) : null;
    const v = vorbereitung ?? null;
    if (!v) {
      gross = t("einsatz.uhr.vorbereitungStehtAus");
      satz = true;
      unter = [ort];
    } else if (v.gesamt === 0) {
      gross = t("einsatz.uhr.vorbereitungOhneFragen");
      satz = true;
      unter = [ort];
    } else if (v.beantwortet === null || v.offen === null) {
      gross = String(v.pflichtGesamt);
      einheit = t("einsatz.uhr.pflichtangaben");
      unter = [ort];
    } else {
      gross = String(v.beantwortet);
      einheit = t("einsatz.uhr.vonGesamt", { gesamt: v.gesamt });
      if (v.offen === 0) { zustand = "fertig"; fertigWort = t("einsatz.uhr.vorbereitungVollstaendig"); }
      unter = [
        v.offen ? t("einsatz.vorbereitung.pflichtOffen", { zahl: v.offen }) : t("einsatz.vorbereitung.pflichtFertig"),
        ort,
      ];
    }
  } else {
    titel = t("einsatz.uhr.beginn");
    gross = beginnIso ? uhrVon(beginnIso) : t("allgemein.strich");
    rechts = beginnIso ? tagVon(beginnIso) : null;
    unter = [ort];
  }
  if (laeuft?.wartet) zustand = "wartet";
  unter = unter.filter(Boolean);

  return (
    <section className={`ez-uhr ist-${zustand}`} aria-live="polite">
      <div className="ez-uhr-kopf">
        <span className="ez-uhr-titel">{titel}</span>
        {zustand === "laeuft" || zustand === "wartet" ? (
          <span className="ez-uhr-zustand">
            <span className="einsatz-puls" aria-hidden="true" />
            {t(zustand === "wartet" ? "einsatz.uhr.laeuftGeraet" : "einsatz.uhr.laeuft")}
          </span>
        ) : zustand === "fertig" && s.zustand !== "abgeschlossen" ? (
          <span className="ez-uhr-zustand fertig">{fertigWort}</span>
        ) : null}
      </div>
      <div className="ez-uhr-mitte">
        <span className={`ez-uhr-zahl${satz ? " ist-satz" : ""}`}>
          {gross}
          {einheit ? <small>{einheit}</small> : null}
        </span>
        {rechts ? <span className="ez-uhr-rechts">{rechts}</span> : null}
      </div>
      {unter.length ? <span className="ez-uhr-unter">{unter.join(` ${t("allgemein.trennpunkt")} `)}</span> : null}
    </section>
  );
}
